import { IconNames } from '@dynamic/ui';
import { t } from 'i18next';
import React, {
  Dispatch,
  useCallback,
  useMemo,
  useReducer,
  useState,
} from 'react';

import { FormSubmissionResTypes, TFormData, TFormResponse } from '#/client';
import { PaymentFormKey } from '#/plugins/paywall/components';
import { FormInputVariants } from '#/service/constants';
import { Dictionary } from '#/service/utils';
import { generalModalActions } from '#/store/modals';

import * as Styled from './Form.styled';
import {
  Action,
  FormAction,
  FormProps,
  FormState,
  InputButton,
  InputButtonSubmit,
  InputGroup,
  InputGroupType,
  TInputState,
  TValidation,
  ValidationListType,
} from './Form.types';
import { FormGroupButton } from './FormButton';
import FormInput from './FormInput';
import { getMainErrorMessage } from './formUtils';

// TODO - this map should be replaced with icon names from response
const iconMap: Dictionary<string, IconNames> = {
  email: 'envelope',
  password: 'lock',
  password_confirmation: 'lock',
  'user.city': 'city',
  'user.country': 'globe',
  username: 'envelope',
  [PaymentFormKey.CardHolderName]: 'avatar',
  [PaymentFormKey.CardNumber]: 'creditcard',
  [PaymentFormKey.ExpirationDate]: 'calendar',
  [PaymentFormKey.SecurityCode]: 'lock',
  [PaymentFormKey.BillingZipCode]: 'city',
};

const getInitialState = (inputGroups: InputGroup[]) => {
  return inputGroups.reduce(
    (state: FormState, group: InputGroup) => {
      if (group.type === InputGroupType.Field) {
        group.inputs.forEach(({ key, defaultValue }) => {
          state.inputs[key] = {
            touched: false,
            // TODO#liquid: Liquid returns string, but we need boolean values here.
            // figure out how to handle cases like this.
            value:
              defaultValue === 'true'
                ? true
                : defaultValue === 'false'
                  ? false
                  : defaultValue,
          };
        });
      }

      return state;
    },
    { inputs: {}, errorMessages: {} }
  );
};

const reducer = (formState: FormState, action: Action): FormState => {
  const { type, payload } = action;
  const { errorMessages, inputState, key } = payload;

  switch (type) {
    case FormAction.Update:
      return {
        ...formState,
        inputs: {
          ...formState.inputs,
          [key]: {
            ...formState.inputs[key],
            ...inputState,
          },
        },
      };

    case FormAction.Error:
      return {
        ...formState,
        errorMessages: {
          ...formState.errorMessages,
          [key]: errorMessages,
        },
      };

    case FormAction.Reset:
      return action.payload;

    default:
      return formState;
  }
};

const clearForm = (inputGroups: InputGroup[], dispatch: Dispatch<Action>) => {
  const action = {
    type: FormAction.Reset,
    payload: getInitialState(inputGroups),
  };

  dispatch(action);
};

const Form = ({
  inputGroups,
  registrationHackMode,
  isWide,
  ...rest
}: FormProps) => {
  // TODO: this should be typed
  const [formState, dispatch] = useReducer(
    reducer,
    getInitialState(inputGroups)
  );
  const [activeButtonKey, setActiveButtonKey] = useState<string>();
  const [isFormValid, setIsFormValid] = useState<boolean>();
  const validationList = useMemo(
    () =>
      inputGroups.reduce((list: ValidationListType[], inputGroup) => {
        if (inputGroup.type === InputGroupType.Field) {
          inputGroup.inputs.forEach((input) => {
            list.push({
              key: input.key,
              groupKey: inputGroup.key,
              validations: input.validations ?? [],
            });
          });
        }

        return list;
      }, []),
    [inputGroups]
  );

  const checkValidity = (ref: HTMLFormElement) => {
    const isValid = ref?.checkValidity();

    if (isValid === isFormValid) {
      return;
    }

    setIsFormValid(isValid);
  };

  const updateState = (key: string, inputState: TInputState) => {
    const action = {
      type: FormAction.Update,
      payload: { key, inputState },
    };

    dispatch(action);
  };

  // allow a field to get the value of another field for cross-field validation
  const getFieldValue = useCallback(
    (key: string = '') => {
      if (
        !(
          formState.inputs &&
          Object.prototype.hasOwnProperty.call(formState.inputs, key)
        )
      ) {
        console.debug(`Form - no input field for [${key}].`);

        return;
      }

      return formState.inputs[key].value;
    },
    [formState.inputs]
  );

  const updateErrorMessages = useCallback(
    (key?: string, ...errorMessages: (string | undefined)[]) => {
      const action = {
        type: FormAction.Error,
        payload: { key, errorMessages: errorMessages.filter(Boolean) },
      };

      dispatch(action);
    },
    []
  );

  const getFormData = useCallback(() => {
    return Object.keys(formState.inputs).reduce(
      (formData: TFormData, key: string) => {
        formData[key] = formState.inputs[key].value;

        return formData;
      },
      {}
    );
  }, [formState.inputs]);

  const handleSubmitError = useCallback(
    (res: TFormResponse) => {
      const { message, error: { validation = {} } = {} } = res?._meta ?? {};
      const validationEntries = Object.entries(validation);
      // display validation errors under each invalid field
      validationEntries.forEach(([key, errorMessages]) => {
        if (formState.inputs[key]) {
          updateErrorMessages(key, ...errorMessages);
        }
      });
      // display the modal with an error message
      generalModalActions.showModal({
        buttonLabel: t('button.ok'),
        message: getMainErrorMessage(validationEntries, message),
      });
    },
    [formState.inputs, updateErrorMessages]
  );

  const processValidations = useCallback(
    (validations?: TValidation[], inputValue?: string) => {
      let msg: string | undefined;
      validations?.forEach((validation) => {
        // the single space fallback shows the red border, with no message. we might not get a proper message from Core with some providers.
        if (!validation.message) {
          validation.message = ' ';
        }
        const { message, regexp, match } = validation;

        // we don't validate empty. we have the "required" prop for that
        if (!inputValue) {
          return;
        }

        // regex validation
        if (regexp && !RegExp(regexp).test(inputValue)) {
          msg = message;

          return;
        }

        // field match validation
        if (match) {
          const otherInputValue = getFieldValue(match);

          if (inputValue !== otherInputValue) {
            msg = message;

            return;
          }
        }
      });

      return msg;
    },
    [getFieldValue]
  );

  const handleValidate = useCallback(
    (
      dataKey: string,
      groupKey: string,
      inputRef?: React.MutableRefObject<HTMLInputElement>,
      value?: string
    ) => {
      inputRef?.current?.setCustomValidity('');
      let msg = inputRef?.current?.validationMessage ?? undefined;
      const inputValue = value ?? formState.inputs[dataKey].value?.toString();
      const validations = validationList.find(
        (validationItem) =>
          validationItem.key === dataKey && validationItem.groupKey === groupKey
      )?.validations;

      const validationMessage = processValidations(validations, inputValue);

      if (validationMessage !== undefined) {
        msg = validationMessage;
      }

      const currentGroup = inputGroups.find((group) => group.key === groupKey);

      // check if other input from field match has same value and if there's any error message shown
      if (currentGroup?.type === InputGroupType.Field && !msg) {
        let localMsg;
        const matchedField = currentGroup.inputs.find(
          (input) =>
            input?.validations?.some((validation) => {
              if (validation.match === dataKey) {
                localMsg = validation.message;

                return true;
              }

              return false;
            })
        );
        const otherInputValue = getFieldValue(matchedField?.key);

        updateErrorMessages(
          matchedField?.key,
          otherInputValue && inputValue !== otherInputValue
            ? localMsg
            : undefined
        );
      }
      updateErrorMessages(dataKey, msg);
    },
    [
      formState.inputs,
      getFieldValue,
      inputGroups,
      processValidations,
      updateErrorMessages,
      validationList,
    ]
  );

  const handleSubmit = useCallback(
    (
      buttonKey: string,
      submitForm: (data: TFormData) => Promise<TFormResponse>
    ) => {
      setActiveButtonKey(buttonKey);
      submitForm(getFormData())
        .then((res) => {
          const { type, ignoreError } = res?._meta ?? {};

          // TODO#registrationHackMode: temporary hack while we need `setTimeout` for `showPaywall` after registration.
          // should be: `.finally(() => setActiveButtonKey(undefined))`
          if (!registrationHackMode) {
            setActiveButtonKey(undefined);
          }

          if (type === FormSubmissionResTypes.SUCCESS) {
            // TODO#registrationHackMode: same as above
            // should be just `clearForm(inputGroups, dispatch)`
            if (!registrationHackMode) {
              // TODO: why do we need this exactly?
              clearForm(inputGroups, dispatch);
            }
          } else if (type === FormSubmissionResTypes.ERROR) {
            setActiveButtonKey(undefined);

            if (!ignoreError) {
              handleSubmitError(res);
            }
          }
        })
        .catch(() => setActiveButtonKey(undefined));
    },
    [getFormData, handleSubmitError, inputGroups, registrationHackMode]
  );

  const isButtonDisabled = useCallback(
    (button: InputButton) => {
      if (
        activeButtonKey ||
        Object.values(formState.errorMessages).some(
          (errorMessage) => errorMessage?.length
        )
      ) {
        return true;
      }

      if (button.variant !== FormInputVariants.SUBMIT && !button.isMainButton) {
        return false;
      }

      return !isFormValid;
    },
    [activeButtonKey, formState.errorMessages, isFormValid]
  );

  const handleFormSubmit = useCallback(
    (event) => {
      event.preventDefault();

      const buttons = inputGroups.find((g) => g.type === InputGroupType.Button)
        ?.inputs as InputButton[];
      const submitButton = buttons.find(
        (i) => i.variant === FormInputVariants.SUBMIT
      ) as InputButtonSubmit;

      if (submitButton) {
        handleSubmit(submitButton.key, submitButton.onClick);
      }
    },
    [handleSubmit, inputGroups]
  );

  return (
    <Styled.Form
      $isWideForm={isWide}
      onSubmit={handleFormSubmit}
      ref={checkValidity}
      {...rest}
    >
      {inputGroups.map((group) => {
        if (group.type === InputGroupType.Button) {
          return (
            <Styled.FormButtonContainer $isWideForm={isWide} key={group.key}>
              {group.inputs.map((button) => (
                <FormGroupButton
                  activeButtonKey={activeButtonKey}
                  button={button}
                  getFormData={getFormData}
                  handleSubmit={handleSubmit}
                  isButtonDisabled={isButtonDisabled}
                  key={button.key}
                />
              ))}
            </Styled.FormButtonContainer>
          );
        }

        if (group.type === InputGroupType.Field) {
          return group.inputs.map(({ key, ...rest }) => (
            <FormInput
              dataKey={key}
              errorMessages={formState.errorMessages[key]}
              groupKey={group.key}
              handleValidate={handleValidate}
              icon={iconMap[key]}
              key={key}
              state={formState.inputs[key]}
              updateErrorMessages={updateErrorMessages}
              updateState={updateState}
              {...rest}
            />
          ));
        }

        return null;
      })}
    </Styled.Form>
  );
};

Form.displayName = 'Form';

export default Form;
